import { Dialog, DialogBareContent, DialogOverlay, DialogPortal, DialogTitle } from '@h2hspark/ui';
import { ArrowLeft, X } from 'lucide-react';
import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { useOverlayFocusReturn } from '../../member/use-overlay-focus-return';

import { ATTENDEE_DETAIL_CLOSE_LABEL } from './constants';

/**
 * **The shell §2.16's person card is drawn in when it is opened over a list** (`MEM-056` AC-3).
 *
 * ## Why the two detail screens are still routes
 *
 * `/app/events/:id/attendees/:ref` and `/app/events/:id/matches/:ref` are addresses: `MEM-047`
 * pins them in mailed links, and a member who bookmarks somebody keeps them. So neither becomes a
 * piece of a list page's state. What changed on 2026-09-12 is that a link **from** a list carries
 * its own location in `state.backgroundLocation`, and `App.tsx` then renders the list underneath
 * and the detail in this shell over it — React Router's own modal-route arrangement. A visit with
 * no background — a mail, a bookmark, a reload of a shared URL — renders the standalone page
 * instead, unchanged. This is the ticket's arrangement of 2026-09-11 (ruling 21) with the address
 * kept rather than replaced.
 *
 * The list underneath never unmounts, which is what makes `MEM-056` AC-3's *"back from it restores
 * the list beneath at its scroll position"* true without a scroll-restoration trick.
 *
 * ## One element, two shapes
 *
 * §2.16 draws a `rgba(28,14,48,.55)` scrim over a 460px card at `--radius-xl` with `--shadow-xl`,
 * top-aligned with 24px of padding. Below `md` that card is the whole screen, which is the phone
 * arrangement `MEM-056`'s overlay table gives for the attendee detail. Radix supplies the
 * behaviour at either width — Escape, the scrim, the focus trap — and none of that is less true on
 * a phone.
 *
 * **Focus return is ours rather than Radix's, and it has to be.** This overlay is a *route*, so
 * closing it re-renders the list underneath and the row that opened it is a new DOM node; the
 * reference Radix kept is detached by then and focusing it does nothing, which dropped the
 * keyboard on `<body>`. `useOverlayFocusReturn` cancels Radix's restore and finds the row again by
 * the address it links to — see that hook's header, and the parity report's (a)-1.
 *
 * **The way out changes with the width, and that is a capability rather than a style** — the same
 * argument `messages/thread-dialog.tsx` makes, and it is why `responsive-behaviour.ts` carries an
 * entry for this file. On a phone the control is a back arrow at the top left, where the platform's
 * own back gesture lives; above `md` it is the ✕ at the top right of the gradient header, over a
 * scrim that also closes. Both land back on the list.
 */
export function AttendeeDetailOverlay({
  title,
  onClose,
  children,
}: {
  /** The dialog's accessible name. The card's own header carries the person visually. */
  readonly title: string;
  readonly onClose: () => void;
  readonly children: React.ReactNode;
}): React.ReactElement {
  // This overlay's own address is what the row that opened it links to, and the handle the focus
  // return finds it by after the remount.
  const { onCloseAutoFocus } = useOverlayFocusReturn(useLocation().pathname);
  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogPortal>
        {/* §2.16's scrim, from `md` up. Below it the card is the whole screen, so a scrim behind
            it would be a tap target nobody can reach. */}
        <DialogOverlay className="hidden bg-[rgba(28,14,48,.55)] backdrop-blur-none md:block" />
        {/*
          **The scroll lives outside the card, which is §2.16's own arrangement** and not a
          detail: the overlay wrapper is `align-items:flex-start; padding:24px; overflow-y:auto`
          and the card is whatever height its contents are. Capping the card at
          `100vh - 48px` and scrolling *inside* it put the two actions behind an internal
          scrollbar on a 900px-tall window, which is the one thing on this card a member came to
          press.

          So this element is the scroll layer at every width: the screen itself below `md`, and a
          transparent 24px-padded sheet above it with the card centred in it. Covering the
          viewport means Radix's own outside-click never fires, so the click on the padding is
          handled here — `e.target === e.currentTarget` is the padding and nothing else, which is
          the prototype's `onClick={closePerson}` with `stopClick` on the card.
        */}
        <DialogBareContent
          data-testid="attendee-detail-dialog"
          className="fixed inset-0 z-50 overflow-y-auto bg-background md:bg-transparent md:p-6"
          aria-describedby={undefined}
          onCloseAutoFocus={onCloseAutoFocus}
          onClick={(event) => {
            if (event.target === event.currentTarget) onClose();
          }}
        >
          <DialogTitle className="sr-only">{title}</DialogTitle>
          <div className="md:mx-auto md:w-full md:max-w-[460px] md:overflow-hidden md:rounded-xl md:shadow-xl">
            {children}
          </div>
        </DialogBareContent>
      </DialogPortal>
    </Dialog>
  );
}

/**
 * The way out, laid over the card's gradient header.
 *
 * Two controls rather than one restyled, because they are in different corners and only one of
 * them is in a thumb's reach: `ArrowLeft` at the top left below `md`, §2.16's 34px round ✕ at the
 * top right above it. Both call the same handler.
 */
export function AttendeeDetailCloseControl({
  onClose,
  backLabel,
}: {
  readonly onClose: () => void;
  /** What the phone control returns to, e.g. *Back to who's going*. Its accessible name. */
  readonly backLabel: string;
}): React.ReactElement {
  return (
    <>
      <button
        type="button"
        onClick={onClose}
        aria-label={backLabel}
        className="absolute left-4 top-4 inline-flex h-[34px] w-[34px] items-center justify-center rounded-pill bg-white/20 text-white md:hidden"
      >
        <ArrowLeft className="h-[18px] w-[18px]" aria-hidden />
      </button>
      <button
        type="button"
        onClick={onClose}
        aria-label={ATTENDEE_DETAIL_CLOSE_LABEL}
        className="absolute right-4 top-4 hidden h-[34px] w-[34px] items-center justify-center rounded-pill bg-white/20 text-white md:inline-flex"
      >
        <X className="h-[18px] w-[18px]" aria-hidden />
      </button>
    </>
  );
}

/**
 * What a link from a list must carry for the detail to open **over** it rather than instead of it.
 *
 * `<Link to={detailPath} state={useOverlayLinkState()}>` and nothing else: `App.tsx` reads
 * `backgroundLocation` off the location it lands on, renders the list from it, and matches the
 * real address in its overlay tree. A link without this state is not broken — it navigates to the
 * standalone page, which is what a mailed link does.
 *
 * A hook rather than a constant because the value is the caller's *current* location, and it is
 * here rather than in each of the two cards so the key is written once.
 */
export function useOverlayLinkState(): {
  readonly backgroundLocation: ReturnType<typeof useLocation>;
} {
  const location = useLocation();
  return React.useMemo(() => ({ backgroundLocation: location }), [location]);
}
